import React from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ResizableHeaderCell from "./ResizableHeaderCell";
import CatalogCellEditDialog from "./CatalogCellEditDialog";
import CatalogLoadingPlaceholder from "./CatalogLoadingPlaceholder";
import CatalogNativeSelect from "./CatalogNativeSelect";
import { useResizableColumns } from "./hooks/useResizableColumns";
import { MAPPING_COLUMN_WIDTHS } from "./utils/catalogTableLayout";
import { getFeedColumnLabel } from "./utils/catalogMappingTableUtils";
import {
  buildImportCellStateClasses,
  buildImportIssuesColumnClass,
  feedColumnHasImportError,
  feedColumnHasImportWarning,
  feedColumnMatchesSkipRule,
  formatFeedRowErrors,
  getFeedRowImportIssues,
} from "./utils/catalogFieldLabels";
import { resolveColumnDefaultOverlays, flattenColumnDefaultGroups } from "./utils/columnDefaultRulesUtils";
import { buildMappingFieldSelectOptions } from "./utils/mappingTokenCategoryUtils";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import { highlightFeedLineWhenReady } from "./utils/scrollFeedLineIntoView";

const SKIP_VALUE = "skip";

const resolvePreviewRowIdentity = (row, rowIndex, pageOffset) => {
  const sourceRowIndex = Number(row?.source_row_index);
  const absoluteIndex = Number.isFinite(sourceRowIndex) ? sourceRowIndex : pageOffset + rowIndex;
  const feedRow = Number(row?.feed_row) || absoluteIndex + 1;
  return { absoluteIndex, feedRow };
};

const MappingPreviewMatrix = ({
  columnNumbers,
  headerLabels,
  previewRows,
  mapping,
  fieldKeys,
  onMappingChange,
  pageOffset = 0,
  showErrorColumn = false,
  rowErrors = null,
  rowWarnings = null,
  importData = null,
  onCellSave,
  onDeleteRow,
  isHeadless = false,
  loading = false,
  rowSkipRules = [],
  showSkipMatchesOnly = false,
  columnDefaultRules = {},
  showColumnDefaultOverlays = false,
  scrollTargetFeedRow = null,
  onScrollTargetHandled,
  readOnly = false,
}) => {
  const [editState, setEditState] = React.useState(null);
  const [draftValue, setDraftValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const tableWrapRef = React.useRef(null);

  const resizeKeys = React.useMemo(
    () => [
      "__index__",
      ...(showErrorColumn ? ["__error__"] : []),
      ...columnNumbers,
    ],
    [columnNumbers, showErrorColumn],
  );

  const { getWidth, getCellStyle, startResize, tableMinWidth } = useResizableColumns(resizeKeys, {
    defaultWidths: MAPPING_COLUMN_WIDTHS,
    defaultWidth: 160,
    minWidth: 80,
    storageKey: "cm-mapping-col-widths",
  });

  const assignableFieldKeys = React.useMemo(() => {
    const legacyAssigned = Object.values(mapping || {}).filter(
      (field) => field && field !== SKIP_VALUE && !fieldKeys.includes(field),
    );
    return [...new Set([...fieldKeys, ...legacyAssigned])];
  }, [fieldKeys, mapping]);

  const displayRows = previewRows || [];

  const hasColumnDefaultRules = flattenColumnDefaultGroups(columnDefaultRules).length > 0;

  const columnDefaultOverlaysByRow = React.useMemo(() => {
    if (!showColumnDefaultOverlays || !hasColumnDefaultRules) {
      return [];
    }
    return displayRows.map((row) => resolveColumnDefaultOverlays(
      row,
      columnDefaultRules,
      headerLabels,
      mapping,
    ));
  }, [displayRows, showColumnDefaultOverlays, hasColumnDefaultRules, columnDefaultRules, headerLabels, mapping]);

  const openEditor = (rowIndex, column, currentValue) => {
    if (readOnly) {
      return;
    }
    setEditState({ rowIndex, column, currentValue });
    setDraftValue(currentValue == null ? "" : String(currentValue));
  };

  const closeEditor = () => {
    if (saving) {
      return;
    }
    setEditState(null);
    setDraftValue("");
  };

  const handleSave = async () => {
    if (!editState || !onCellSave) {
      return;
    }
    setSaving(true);
    try {
      await onCellSave({
        row_index: editState.rowIndex,
        column: editState.column,
        value: draftValue,
      });
      setEditState(null);
      setDraftValue("");
    } finally {
      setSaving(false);
    }
  };

  const emptyColSpan = Math.max(columnNumbers.length + (showErrorColumn ? 2 : 1), 2);

  React.useEffect(() => {
    if (!scrollTargetFeedRow || loading) {
      return undefined;
    }
    return highlightFeedLineWhenReady(
      tableWrapRef.current,
      scrollTargetFeedRow,
      onScrollTargetHandled,
    );
  }, [scrollTargetFeedRow, loading, previewRows, pageOffset, onScrollTargetHandled]);

  return (
    <>
      <TableContainer
        ref={tableWrapRef}
        className={`cm-table-wrap cm-mapping-table-wrap${loading ? " is-loading" : ""}`}
      >
        {loading ? (
          <CatalogLoadingPlaceholder />
        ) : (
        <Table
          size="small"
          stickyHeader
          className="cm-table cm-mapping-table"
          sx={{ tableLayout: "fixed", minWidth: tableMinWidth, width: tableMinWidth }}
        >
          <TableHead>
            <TableRow className="cm-mapping-sticky-labels">
              <ResizableHeaderCell
                columnKey="__index__"
                width={getWidth("__index__")}
                onResizeStart={startResize}
                className="cm-mapping-corner"
                rowSpan={2}
              >
                #
              </ResizableHeaderCell>
              {showErrorColumn && (
                <ResizableHeaderCell
                  columnKey="__error__"
                  width={getWidth("__error__")}
                  onResizeStart={startResize}
                  className="cm-mapping-feed-header cm-mapping-error-header"
                  rowSpan={2}
                >
                  <>
                    Errori e
                    <br />
                    Warnings
                  </>
                </ResizableHeaderCell>
              )}
              {columnNumbers.map((column) => (
                <ResizableHeaderCell
                  key={`label-${column}`}
                  columnKey={column}
                  width={getWidth(column)}
                  onResizeStart={startResize}
                  className="cm-mapping-feed-header"
                  title={headerLabels?.[column] || ""}
                >
                  {getFeedColumnLabel(column, headerLabels, isHeadless)}
                </ResizableHeaderCell>
              ))}
            </TableRow>
            <TableRow className="cm-mapping-sticky-selectors">
              {columnNumbers.map((column) => {
                const mappingOptions = buildMappingFieldSelectOptions({
                  assignableFieldKeys,
                  mapping,
                  columnDefaultRules,
                  currentColumn: column,
                  skipValue: SKIP_VALUE,
                });

                return (
                <TableCell
                  key={`map-${column}`}
                  className="cm-mapping-db-header"
                  style={getCellStyle(column)}
                >
                  <CatalogNativeSelect
                    className="cm-mapping-field-select"
                    value={mapping[column] || SKIP_VALUE}
                    title={CM_TOOLTIPS.mappingFieldSelect}
                    onChange={(nextValue) => onMappingChange(column, nextValue)}
                    options={mappingOptions}
                    ariaLabel={`Mapping colonna ${column}`}
                    menuClassName="cm-mapping-select-menu"
                    disabled={readOnly}
                  />
                </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row, rowIndex) => {
              const { absoluteIndex, feedRow } = resolvePreviewRowIdentity(row, rowIndex, pageOffset);
              const { errors: fieldErrors, warnings: fieldWarnings } = getFeedRowImportIssues(
                rowErrors,
                rowWarnings,
                feedRow,
                importData,
              );
              const rowHasError = Boolean(fieldErrors);
              const rowHasWarning = Boolean(fieldWarnings);
              const errorText = formatFeedRowErrors(rowErrors, feedRow, importData);
              const issuesColumnClass = buildImportIssuesColumnClass({
                hasError: rowHasError,
                hasWarning: rowHasWarning,
              });
              return (
                <TableRow key={`preview-row-${absoluteIndex}`} data-feed-line={feedRow}>
                  <TableCell
                    className="cm-mapping-row-index"
                    style={getCellStyle("__index__")}
                  >
                    <div className="cm-row-index-cell">
                      <span>{feedRow}</span>
                      {onDeleteRow && !readOnly && (
                        <IconButton
                          size="small"
                          className="cm-btn-danger cm-row-delete-btn"
                          title={CM_TOOLTIPS.eliminaRigaNormalizzata}
                          onClick={() => onDeleteRow(absoluteIndex)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                  {showErrorColumn && (
                    <TableCell
                      className={issuesColumnClass}
                      style={getCellStyle("__error__")}
                      title={errorText}
                    >
                      <Typography component="div" className="cm-cell-clamp">
                        {errorText || "—"}
                      </Typography>
                    </TableCell>
                  )}
                  {columnNumbers.map((column) => {
                    const value = row[column] || "";
                    const cellSkipMatch = feedColumnMatchesSkipRule(row, column, rowSkipRules, headerLabels);
                    const defaultOverlayValue = columnDefaultOverlaysByRow[rowIndex]?.[column];
                    const cellHasError = feedColumnHasImportError(fieldErrors, column, mapping, SKIP_VALUE);
                    const cellHasWarning = feedColumnHasImportWarning(fieldWarnings, column, mapping, SKIP_VALUE);
                    const cellClassName = [
                      buildImportCellStateClasses({
                        hasError: cellHasError,
                        hasWarning: cellHasWarning,
                        hasSkip: cellSkipMatch,
                      }),
                      defaultOverlayValue ? "cm-cell-has-default-overlay" : "",
                    ].filter(Boolean).join(" ");
                    const cellStyle = getCellStyle(column);
                    return (
                      <TableCell
                        key={`${absoluteIndex}-${column}`}
                        className={cellClassName}
                        style={cellStyle}
                        sx={cellSkipMatch && !cellHasError && !cellHasWarning
                          ? { bgcolor: "#e6f4ff" }
                          : undefined}
                        onDoubleClick={!readOnly && onCellSave ? () => openEditor(absoluteIndex, column, value) : undefined}
                        title={
                          readOnly
                            ? (value || "")
                            : (value ? `${value} (doppio click per modificare)` : "Doppio click per modificare")
                        }
                      >
                        <Typography component="div" title={value} className="cm-cell-clamp">
                          {value || "—"}
                        </Typography>
                        {defaultOverlayValue && (
                          <span className="cm-column-default-overlay" title={`Default: ${defaultOverlayValue}`}>
                            {defaultOverlayValue}
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {displayRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={emptyColSpan} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </TableContainer>

      <CatalogCellEditDialog
        open={Boolean(editState)}
        table="normalized"
        column={editState?.column}
        currentValue={editState?.currentValue}
        draftValue={draftValue}
        saving={saving}
        onClose={closeEditor}
        onChange={setDraftValue}
        onSave={handleSave}
      />
    </>
  );
};

MappingPreviewMatrix.propTypes = {
  columnNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerLabels: PropTypes.object,
  previewRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  mapping: PropTypes.object.isRequired,
  fieldKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onMappingChange: PropTypes.func.isRequired,
  pageOffset: PropTypes.number,
  showErrorColumn: PropTypes.bool,
  rowErrors: PropTypes.object,
  rowWarnings: PropTypes.object,
  importData: PropTypes.object,
  onCellSave: PropTypes.func,
  onDeleteRow: PropTypes.func,
  isHeadless: PropTypes.bool,
  loading: PropTypes.bool,
  rowSkipRules: PropTypes.arrayOf(PropTypes.shape({
    column: PropTypes.string,
    value: PropTypes.string,
  })),
  showSkipMatchesOnly: PropTypes.bool,
  columnDefaultRules: PropTypes.object,
  showColumnDefaultOverlays: PropTypes.bool,
  scrollTargetFeedRow: PropTypes.number,
  onScrollTargetHandled: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default MappingPreviewMatrix;
